'use client';

import { ServiceLayer } from '@packages/modules/src/ServiceLayer/ServiceLayer.client';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks/useLayerTracking/useLayerTracking';
import { type DeliverySize } from '@packages/gql/generated/shopping/graphql';
import { useIntl } from 'react-intl';
import { TextWithDialog } from '@packages/modules/src/TextWithDialog/TextWithDialog.client';
import { carrierDeliveryInfoMessages } from './messages';

export type CarrierDeliveryInfoProps = {
  /**
   * Delivery size used to select the carrier-delivery text and service-layer content.
   */
  deliverySize: DeliverySize;
};

/**
 * Renders carrier-delivery information and a dialog trigger for supported delivery sizes.
 *
 * Supported sizes:
 * - `S`: delivery to home
 * - `L`: delivery to property boundary
 *
 * Dialog content is loaded from the service-layer CMS entry.
 */

export const CarrierDeliveryInfo = ({ deliverySize }: CarrierDeliveryInfoProps) => {
  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();

  if (!(deliverySize === 'S' || deliverySize === 'L')) return null;

  const dialogHeadline = formatMessage(carrierDeliveryInfoMessages.dialogHeadline);

  /**
   * Tracks dialog-open events with a size-specific tracking label.
   */
  const onDialogOpenHandler = () => {
    trackViewLayer(`${dialogHeadline} ${deliverySize}`);
  };

  return (
    <div className="text-size-body-sm flex flex-row items-center gap-1">
      <span className="icon-emp-delivery block size-3 shrink-0" />
      <span>
        {formatMessage(
          deliverySize === 'S'
            ? carrierDeliveryInfoMessages.carrierDeliveryS
            : carrierDeliveryInfoMessages.carrierDeliveryL,
          {
            link: (msg) => (
              <TextWithDialog
                message={msg.join(' ')}
                key="carrierDeliveryInfo"
                dialogTitle={dialogHeadline}
                dialogContent={
                  <ServiceLayer serviceLayerId={`delivery_spedition-layer-${deliverySize}`} />
                }
                onOpenDialogHandler={onDialogOpenHandler}
                displayTrackingProps={{ category: 'dv-service-layer-carrier-delivery-info' }}
              />
            ),
          },
        )}
      </span>
    </div>
  );
};
